<template>
  <v-container fluid>
    <!-- Point Totals -->
    <v-row class="mt-2">
      <v-col cols="12">
        <h2>Point Totals</h2>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12" sm="6" md="4" v-for="pointType in pointTypes" :key="pointType.id">
        <v-card outlined class="pa-3 text-center">
          <div class="text-subtitle-1">{{ pointType.name }}</div>
          <div class="text-h5">{{ pointType.bank }}</div>
        </v-card>
      </v-col>
      <v-col cols="12" v-if="!pointTypesLoading && pointTypes.length === 0">
        <span class="grey--text">No point types yet. Add one on the Habits page.</span>
      </v-col>
    </v-row>

    <v-divider class="my-6"></v-divider>

    <!-- Rewards -->
    <v-row>
      <v-col cols="12">
        <h2>Rewards</h2>
      </v-col>
    </v-row>
    <v-data-table
      :headers="rewardHeaders"
      :items="rewards"
      :loading="rewardsLoading"
      hide-default-footer
      class="elevation-1">
      <template v-slot:[`item.point_type`]="{ item }">
        {{ pointTypeName(item.point_type) }}
      </template>
      <template v-slot:[`item.actions`]="{ item }">
        <div class="d-flex flex-wrap justify-end align-center py-1">
          <core-btn-primary
            small
            :disabled="!canAfford(item)"
            :loading="purchasingId === item.id"
            :aria-label="`Purchase ${item.name}`"
            @click="purchase(item)">
            Purchase<v-icon right small class="ml-1">mdi-cart</v-icon>
          </core-btn-primary>
          <v-btn
            small
            icon
            color="bad"
            :aria-label="`Delete ${item.name}`"
            @click="confirmDelete(item)">
            <v-icon>mdi-delete</v-icon>
          </v-btn>
        </div>
      </template>
    </v-data-table>

    <v-row class="mt-4 align-center d-flex" align="stretch">
      <v-col cols="12" sm="6" md="3">
        <core-text-field
          v-model="newReward.name"
          label="New Reward Name"
          dense
          hide-details="auto"></core-text-field>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <core-select
          v-model="newReward.point_type"
          :items="pointTypes"
          item-text="name"
          item-value="id"
          label="Point Type"
          dense
          hide-details="auto">
        </core-select>
      </v-col>
      <v-col cols="12" sm="6" md="2">
        <core-number-field
          v-model="newReward.point_cost"
          label="Point Cost"
          dense
          hide-details="auto"></core-number-field>
      </v-col>
      <v-col cols="12" sm="6" md="auto" class="d-flex align-center pt-3">
        <core-btn-primary
          :block="$vuetify.breakpoint.xsOnly"
          :loading="rewardSaving"
          :disabled="!canAddReward"
          @click="addReward()">
          Add Reward<v-icon right>mdi-plus</v-icon>
        </core-btn-primary>
      </v-col>
    </v-row>

    <!-- Delete confirmation -->
    <v-dialog v-model="deleteDialog" max-width="400">
      <v-card>
        <v-card-title>Delete Reward</v-card-title>
        <v-card-text>
          Are you sure you want to delete "{{ rewardToDelete && rewardToDelete.name }}"?
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <core-btn-neutral @click="deleteDialog = false">Cancel</core-btn-neutral>
          <core-btn-negative :loading="deleting" @click="deleteReward()">Delete</core-btn-negative>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
export default {
  data() {
    return {
      pointTypes: [],
      pointTypesLoading: true,

      rewards: [],
      rewardsLoading: true,
      newReward: { name: "", point_type: null, point_cost: 1 },
      rewardSaving: false,
      purchasingId: null,

      deleteDialog: false,
      rewardToDelete: null,
      deleting: false,

      rewardHeaders: [
        { text: "Name", value: "name" },
        { text: "Point Type", value: "point_type" },
        { text: "Point Cost", value: "point_cost" },
        { text: "", value: "actions", sortable: false, align: "end" },
      ],
    }
  },
  computed: {
    canAddReward() {
      return !!this.newReward.name && !!this.newReward.point_type
    },
  },
  methods: {
    pointTypeName(id) {
      let pointType = this.pointTypes.find((p) => p.id === id)
      return pointType ? pointType.name : ""
    },
    canAfford(reward) {
      let pointType = this.pointTypes.find((p) => p.id === reward.point_type)
      return !!pointType && pointType.bank >= reward.point_cost
    },
    async loadPointTypes() {
      this.pointTypesLoading = true
      await this.$django_api.get("habits/point_type/").then((response) => {
        this.pointTypes = response.data
      })
      this.pointTypesLoading = false
    },
    async loadRewards() {
      this.rewardsLoading = true
      await this.$django_api.get("habits/reward/").then((response) => {
        this.rewards = response.data
      })
      this.rewardsLoading = false
    },
    async addReward() {
      this.rewardSaving = true
      await this.$django_api.post("habits/reward/", this.newReward).then((response) => {
        this.rewards.push(response.data)
        this.newReward = { name: "", point_type: null, point_cost: 1 }
      })
      this.rewardSaving = false
    },
    async purchase(reward) {
      this.purchasingId = reward.id
      await this.$django_api.post("habits/reward/purchase/", { id: reward.id }).then((response) => {
        let pointType = this.pointTypes.find((p) => p.id === response.data.id)
        if (pointType) pointType.bank = response.data.bank

        let alert = {
          type: "success",
          message: `Purchased "${reward.name}"!`,
        }
        this.$store.dispatch("set_alert", alert)
      })
      this.purchasingId = null
    },
    confirmDelete(reward) {
      this.rewardToDelete = reward
      this.deleteDialog = true
    },
    async deleteReward() {
      this.deleting = true
      await this.$django_api.delete(`habits/reward/?id=${this.rewardToDelete.id}`).then(() => {
        this.rewards = this.rewards.filter((r) => r.id !== this.rewardToDelete.id)
      })
      this.deleting = false
      this.deleteDialog = false
    },
  },
  mounted: async function () {
    await this.loadPointTypes()
    await this.loadRewards()
  },
}
</script>
